import { Fragment, useEffect, useRef, useState } from "react";
import { Button, Card, Col, Form, Row, Spinner } from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import swal from "sweetalert";
import api from "../../../api";
import { initPlaceAutocomplete } from "../../../utils/googleMapsLoader";
import PageTitle from "../../layouts/PageTitle";

const ProjectEdit = () => {
 const navigate = useNavigate();
 const { id } = useParams();
 const locationRef = useRef(null);

 const [loading, setLoading] = useState(true);
 const [saving, setSaving] = useState(false);
 const [clients, setClients] = useState([]);
 const [projectTypes, setProjectTypes] = useState([]);
const [projectCategories, setProjectCategories] = useState([]);
const [offices, setOffices] = useState([]);


 const [form, setForm] = useState({
 projectName: "",
 client: "",
 projectType: "",
 projectCategory: "",
 office: "",
 location: "",
 latitude: "",
 longitude: "",
 });

 /* -----------------------------
 LOAD PROJECT + CLIENTS
 ----------------------------- */
 useEffect(() => {
 const loadData = async () => {
 try {
 const [projectRes, clientsRes, typeRes, categoryRes, officeRes] = await Promise.all([
 api.get(`/projects/${id}`),
 api.get("/clients"),
 api.get("/project-types"),
 api.get("/project-categories"),
 api.get("/offices"),
]);


 if (projectRes.data.success) {
 const p = projectRes.data.data;

 setForm({
 projectName: p.projectName || "",
 client: p.client?._id || "",
 projectType: p.projectType?._id || "",
 projectCategory: p.projectCategory?._id || "",
 office: p.office?._id || "",
 location: p.location || "",
 latitude: p.latitude || "",
 longitude: p.longitude || "",
 });
}

if (clientsRes.data.success) {
 setClients(clientsRes.data.data);
}

if (typeRes.data.success) {
 setProjectTypes(typeRes.data.data);
}

if (categoryRes.data.success) {
 setProjectCategories(categoryRes.data.data);
}

if (officeRes.data.success) {
 setOffices(officeRes.data.data);
}

 } catch (err) {
 swal(
 "Error",
 err.response?.data?.message || "Failed to load project",
 "error"
 );
 navigate("/projects");
 } finally {
 setLoading(false);
 }
 };

 loadData();
 }, [id, navigate]);

 /* -----------------------------
 GOOGLE PLACES AUTOCOMPLETE
 ----------------------------- */
useEffect(() => {
 if (loading) return;
 if (!locationRef.current) return;

 let mounted = true;

 initPlaceAutocomplete(locationRef, ({ formattedAddress, latitude, longitude }) => {
 if (!mounted) return;

 setForm((prev) => ({
 ...prev,
 location: formattedAddress || prev.location,
 latitude: latitude ?? prev.latitude,
 longitude: longitude ?? prev.longitude,
 }));
 }).catch((err) => {
 console.warn("Google Places init warning", err);
 });

 return () => {
 mounted = false;
 };
}, [loading]);
 /* -----------------------------
 FETCH CURRENT LOCATION
 ----------------------------- */
 const fetchCurrentLocation = () => {
 if (!navigator.geolocation) {
 swal("Error", "Geolocation not supported", "error");
 return;
 }

 navigator.geolocation.getCurrentPosition(
 async (pos) => {
 const lat = pos.coords.latitude;
 const lng = pos.coords.longitude;

 setForm((prev) => ({
 ...prev,
 latitude: lat,
 longitude: lng,
 }));

 try {
 const res = await fetch(
 `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${lat}&longitude=${lng}&localityLanguage=en`
 );
 const data = await res.json();

 const address =
 data.city ||
 data.locality ||
 data.principalSubdivision ||
 "Unknown Location";

 setForm((prev) => ({ ...prev, location: address }));

 swal("Success", "Location updated", "success");
 } catch {
 swal("Error", "Failed to fetch address", "error");
 }
 },
 () => swal("Error", "Unable to get location", "error")
 );
 };

 /* -----------------------------
 HANDLE CHANGE
 ----------------------------- */
 const handleChange = (e) => {
 const { name, value } = e.target;
 setForm((prev) => ({ ...prev, [name]: value }));
 };

 /* -----------------------------
 SUBMIT
 ----------------------------- */
 const handleSubmit = async (e) => {
 e.preventDefault();

 if (!form.projectName)
 return swal("Error", "Project Name is required", "error");
 if (!form.client)
 return swal("Error", "Client is required", "error");
 if (!form.projectType)
 return swal("Error", "Project Type is required", "error");
 if (!form.projectCategory)
 return swal("Error", "Project Category is required", "error");
 if (!form.office)
 return swal("Error", "Working Office is required", "error");

 setSaving(true);

 try {
 const res = await api.patch(`/projects/${id}`, form);

 if (res.data.success) {
 swal("Success", "Project updated successfully", "success");
 navigate("/projects");
 } else {
 swal("Error", res.data.message, "error");
 }
 } catch (err) {
 swal(
 "Error",
 err.response?.data?.message || "Failed to update project",
 "error"
 );
 } finally {
 setSaving(false);
 }
 };

 if (loading) {
 return (
 <div className="text-center my-5">
 <Spinner animation="border" />
 </div>
 );
 }

 return (
 <Fragment>
 <PageTitle activeMenu="Edit Project" motherMenu="Projects" />

 <Row>
 <Col lg={12}>
 <Card className="border-0 shadow-sm rounded-4">
 <Card.Header className="d-flex justify-content-between">
 <Card.Title>Edit Project</Card.Title>
 <Button
 variant="secondary"
 onClick={() => navigate("/projects")}
 >
 Back
 </Button>
 </Card.Header>

 <Card.Body>
 <Form onSubmit={handleSubmit}>
 {/* BASIC DETAILS */}
 <h5 className="fw-bold mb-3">1. Basic Project Details</h5>

 <Row className="mb-3">
 <Col md={6}>
 <Form.Group>
 <Form.Label>Project Name *</Form.Label>
 <Form.Control
 name="projectName"
 value={form.projectName}
 onChange={handleChange}
 />
 </Form.Group>
 </Col>

 <Col md={6}>
 <Form.Group>
 <Form.Label>Client *</Form.Label>
 <Form.Select
 name="client"
 value={form.client}
 onChange={handleChange}
 >
 <option value="">Select Client</option>
 {clients.map((c) => (
 <option key={c._id} value={c._id}>
 {c.name || c.clientName}
 </option>
 ))}
 </Form.Select>
 </Form.Group>
 </Col>
 </Row>

 <Row className="mb-3">
 <Col md={6}>
 <Form.Group>
 <Form.Label>Project Type *</Form.Label>
 <Form.Select
 name="projectType"
 value={form.projectType}
 onChange={handleChange}
 >
 <option value="">Select Type</option>
 {projectTypes.map((t) => (
 <option key={t._id} value={t._id}>
 {t.name}
 </option>
 ))}
 </Form.Select>
</Form.Group>

 </Col>

 <Col md={6}>
 <Form.Group>
 <Form.Label>Project Category *</Form.Label>
 <Form.Select
 name="projectCategory"
 value={form.projectCategory}
 onChange={handleChange}
 >
 <option value="">Select Category</option>
 {projectCategories.map((c) => (
 <option key={c._id} value={c._id}>
 {c.name}
 </option>
 ))}
 </Form.Select>
 </Form.Group>
 </Col>

 <Col md={6}>
 <Form.Group>
 <Form.Label>Working Office / Branch *</Form.Label>
 <Form.Select
 name="office"
 value={form.office}
 onChange={handleChange}
 >
 <option value="">Select Office</option>
 {offices.map((o) => (
 <option key={o._id} value={o._id}>
 {o.officeName} ({o.companyName})
 </option>
 ))}
 </Form.Select>
 </Form.Group>
 </Col>
 </Row>

 {/* LOCATION */}
 <h5 className="fw-bold mt-4 mb-3">2. Location Details</h5>

 <div className="mb-3">
 <Button variant="info" onClick={fetchCurrentLocation}>
 Fetch Current Location
 </Button>
 </div>

 <div className="mb-3">
 <Form.Label>Location</Form.Label>
 <Form.Control
 type="text"
 name="location"
 value={form.location}
 onChange={handleChange}
 placeholder="Enter location"
 ref={locationRef}
 />
 <Form.Text className="text-muted">
 Type a place name to get suggestions and auto-fill latitude/longitude.
 </Form.Text>
 </div>

 <div className="mb-3">
 <Form.Label>Selected Location</Form.Label>
 <Form.Control
 type="text"
 value={form.location}
 readOnly
 placeholder="Your chosen location will appear here"
 />
 </div>

 <Row className="mb-4">
 <Col md={6}>
 <Form.Label>Latitude</Form.Label>
 <Form.Control value={form.latitude} readOnly />
 </Col>

 <Col md={6}>
 <Form.Label>Longitude</Form.Label>
 <Form.Control value={form.longitude} readOnly />
 </Col>
 </Row>

 {/* BUTTONS */}
 <div className="d-flex gap-2">
 <Button
 type="submit"
 variant="primary"
 disabled={saving}
 >
 {saving ? "Updating..." : "Update Project"}
 </Button>

 <Button
 variant="danger"
 onClick={() => navigate("/projects")}
 >
 Cancel
 </Button>
 </div>
 </Form>
 </Card.Body>
 </Card>
 </Col>
 </Row>
 </Fragment>
 );
};

export default ProjectEdit;
